// SHADOW NETWORK — Image cropper modal (avatar + banner).
// Uses react-easy-crop for the interactive crop, then exports a JPEG Blob
// via canvas. The parent decides where to upload.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import { motion, AnimatePresence } from "framer-motion";
import { X as XIcon, Loader2, Crop as CropIcon } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tShadow } from "../../i18n/shadow-social";

/**
 * Render a Blob from the original image cropped to the given pixel area.
 * Output is constrained to outputWidth × outputHeight (1500×500 for banner,
 * 600×600 for avatar) for predictable upload sizes.
 */
/**
 * Render a Blob from the original image cropped to the given pixel area.
 * Output is constrained to outputWidth × outputHeight (1500×500 for banner,
 * 600×600 for avatar) for predictable upload sizes.
 */
async function cropToBlob(srcUrl, areaPx, outputW, outputH, mime = "image/jpeg", quality = 0.92) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    // NOTE: We deliberately DO NOT set crossOrigin="anonymous" here.
    // The src is always either a `blob:` or `data:` URL we just created
    // ourselves (same-origin by construction), so setting crossOrigin
    // would (a) be redundant and (b) cause the canvas to be marked as
    // "tainted" in some Safari builds when the file came from another
    // tab/origin, which then makes canvas.toBlob throw a SecurityError.
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("image_decode_failed"));
    i.src = srcUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outputW;
  canvas.height = outputH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  try {
    ctx.drawImage(
      img,
      areaPx.x, areaPx.y, areaPx.width, areaPx.height,
      0, 0, outputW, outputH
    );
  } catch (e) {
    throw new Error("canvas_drawImage_failed: " + (e && e.message ? e.message : "unknown"));
  }
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas_toBlob_returned_null"))),
        mime,
        quality
      );
    } catch (e) {
      reject(new Error("canvas_toBlob_threw: " + (e && e.message ? e.message : "unknown")));
    }
  });
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Prefer URL.createObjectURL over FileReader.readAsDataURL for the cropper
// preview: it streams the binary directly to the <img>, avoiding the cost
// (and ~30% memory blowup) of base64-encoding huge photos in JS. Browsers
// also choke on >100 MB data-URLs while object URLs are unlimited.
function fileToObjectUrl(file) {
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

/**
 * Props:
 *   file: File   — picked from <input type="file">
 *   kind: "avatar" | "banner"
 *   onCancel: () => void
 *   onApply: (blob: Blob, dataUrl: string) => Promise<{ok: boolean, error?: string} | void> | void
 *   busy: bool — external upload status, disables apply button while true
 *   uploadError: string | null — external error message to display
 */
export const ImageCropperModal = ({ file, kind, onCancel, onApply, busy = false, uploadError = null }) => {
  const { lang } = useLanguage();
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [working, setWorking] = useState(false);
  const [localError, setLocalError] = useState(null);

  const cfg = useMemo(() => {
    if (kind === "banner") {
      return { aspect: 3, shape: "rect", outW: 1500, outH: 500, title: tShadow("cropTitleBanner", lang) };
    }
    return { aspect: 1, shape: "round", outW: 600, outH: 600, title: tShadow("cropTitleAvatar", lang) };
  }, [kind, lang]);

  useEffect(() => {
    if (!file) { setSrc(null); return undefined; }
    // Use a blob URL — instant, no base64 cost, no failure on big images.
    const url = fileToObjectUrl(file);
    if (url) {
      setSrc(url);
    } else {
      // Fallback to FileReader if object URLs unavailable (extreme edge case).
      let mounted = true;
      fileToDataUrl(file)
        .then((d) => mounted && setSrc(d))
        .catch(() => mounted && setLocalError("image_decode_failed"));
      return () => { mounted = false; };
    }
    // Cleanup: defer revocation by ~5s. We can't safely revoke synchronously
    // here because react-easy-crop may still be touching the URL during its
    // own unmount tick — revoking too early triggers a hard crash in some
    // browsers ("DOMException: blob URL not found") that propagates as a
    // blank page if it isn't caught by an Error Boundary.
    return () => {
      try {
        setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        }, 5000);
      } catch { /* ignore */ }
    };
  }, [file]);

  const onCropComplete = useCallback((_cropped, _areaPx) => setAreaPx(_areaPx), []);

  const apply = async () => {
    if (!src || !areaPx) return;
    setWorking(true);
    setLocalError(null);
    try {
      const blob = await cropToBlob(src, areaPx, cfg.outW, cfg.outH);
      if (!blob) {
        setLocalError(tShadow("uploadFailedGeneric", lang));
        return;
      }
      // The parent's onApply may return { ok: false, error } on failure so we
      // can surface it inside the cropper instead of just closing silently.
      const res = await onApply(blob, URL.createObjectURL(blob));
      if (res && res.ok === false) {
        setLocalError(res.error || tShadow("uploadFailedGeneric", lang));
      }
    } catch (e) {
      setLocalError(e?.message || tShadow("uploadFailedGeneric", lang));
    } finally {
      setWorking(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget && !busy && !working) onCancel();
  };

  // ESC closes the modal (unless an upload is in flight)
  useEffect(() => {
    if (!file || !src) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy && !working) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [file, src, busy, working, onCancel]);

  if (!file || !src) return null;

  const isWorking = working || busy;

  const overlay = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleBackdrop}
      className="fixed inset-0 z-[9700] flex items-center justify-center p-4"
      style={{ background: "rgba(7,8,15,0.94)", backdropFilter: "blur(8px)" }}
      data-testid="image-cropper-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-[640px] overflow-hidden rounded-2xl border border-amber-400/25 bg-[#0d0e1c] shadow-[0_0_60px_rgba(245,166,35,0.16)]"
        role="dialog"
        aria-modal="true"
        aria-label={cfg.title}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/8 px-5 py-3">
          <div className="flex items-center gap-2">
            <CropIcon className="h-4 w-4 text-amber-300" />
            <h3 className="text-xs font-black uppercase tracking-[0.22em] text-amber-300">
              {cfg.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isWorking}
            aria-label="close"
            data-testid="cropper-close-btn"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-white/55 transition-all hover:border-white/30 hover:text-white disabled:opacity-30"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Crop area */}
        <div
          className="relative w-full bg-black"
          style={{
            height: kind === "banner" ? "min(36vh, 320px)" : "min(56vh, 480px)",
          }}
        >
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={cfg.aspect}
            cropShape={cfg.shape}
            showGrid={kind === "banner"}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            restrictPosition={true}
            style={{
              containerStyle: { background: "#07080f" },
              cropAreaStyle: {
                border: "2px solid rgba(245,166,35,0.85)",
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                color: "rgba(245,166,35,0.85)",
              },
            }}
          />
        </div>

        {/* Controls */}
        <div className="px-5 py-4">
          <p className="mb-3 text-center text-[11px] tracking-[0.04em] text-white/45">
            {tShadow("cropHint", lang)}
          </p>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
              {tShadow("cropZoom", lang)}
            </span>
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="cropper-zoom-slider"
              className="cropper-zoom-range flex-1"
              aria-label="zoom"
            />
            <span className="w-12 text-right font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300">
              {zoom.toFixed(2)}×
            </span>
          </div>
        </div>

        {/* Error banner (visible inside the modal so the user actually sees it) */}
        {(localError || uploadError) && (
          <div
            className="mx-5 mb-2 rounded-lg border border-rose-400/40 bg-rose-500/[0.12] px-3 py-2 text-xs text-rose-200"
            data-testid="cropper-error"
            role="alert"
          >
            {localError || uploadError}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-white/8 bg-black/30 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isWorking}
            data-testid="cropper-cancel-btn"
            className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/65 transition-all hover:border-white/35 hover:text-white disabled:opacity-40"
          >
            {tShadow("cropCancel", lang)}
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={isWorking || !areaPx}
            data-testid="cropper-apply-btn"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:from-amber-300 hover:to-amber-400 disabled:opacity-40"
          >
            {isWorking && <Loader2 className="h-3 w-3 animate-spin" />}
            {isWorking ? tShadow("cropProcessing", lang) : tShadow("cropApply", lang)}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(<AnimatePresence>{overlay}</AnimatePresence>, document.body);
};

export default ImageCropperModal;
